<template>
  <Dialog>
    <DialogContent class="!max-w-[560px]">
      <DialogHeader>
        <div class="flex gap-4 -ml-2">
          <DialogTitle class="!font-normal">
            <span v-if="step == 'step1'">Cancel Plan</span>
            <span v-if="step == 'step2'">One last thing before you go</span>
            <span v-if="step == 'step3'">You have canceled your plan</span>
          </DialogTitle>
        </div>
      </DialogHeader>
      <template v-if="step === 'step1'">
        <div v-if="isLoading" class="flex items-center justify-center h-20">
          <Spinner />
        </div>
        <div v-else class="px-6 py-6">
          <div class="space-y-4">
            <p class="text-lg">What will happen if I cancel my plan?</p>
            <p v-if="nextInvoices.yearly?.date">
              Since you are in a yearly plan, your monthly subscription will
              continue to be charged until the end of the your yearly billing
              cycle, on {{ nextInvoices.yearly?.date }}. You can always remove
              billable users (guest editors or members) to lower your monthly
              cost.
            </p>
            <p>
              Your current plan will downgrade to Arcweave Free on
              <strong>{{ nextInvoice.date }}</strong>
              . You will be able to fully continue using your current plan until
              then.
            </p>
            <p>Your account will be limited to <strong>3 projects</strong>.</p>
          </div>
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <Button type="button" variant="secondary"> Cancel </Button>
          </DialogClose>
          <Button type="submit" variant="danger" @click="step = 'step2'">
            Continue
          </Button>
        </DialogFooter>
      </template>
      <template v-if="step === 'step2'">
        <div class="px-6 py-6">
          <p class="mb-4 text-lg">Please tell us why you are leaving</p>
          <form id="downgrade-form" @submit="onSubmit">
            <div class="space-y-2 mb-2">
              <label
                v-for="(option, index) in options"
                :key="index"
                class="flex flex-row gap-4 items-center cursor-pointer"
              >
                <CheckboxRoot
                  :value="option"
                  class="flex size-[22px] appearance-none items-center justify-center rounded-[4px] bg-primary-900 border border-primary-500"
                  @update:checked="handleChange($event, option)"
                >
                  <CheckboxIndicator
                    class="bg-accent-600 h-full w-full rounded flex items-center justify-center"
                  >
                    <Check class="h-3.5 w-3.5 text-white" />
                  </CheckboxIndicator>
                </CheckboxRoot>
                <span class="select-none text-white">
                  {{ option }}
                </span>
              </label>
            </div>
            <Textarea
              v-if="checkedOptions.includes('Other')"
              v-bind="otherReasonAttrs"
              id="otherReason"
              v-model="otherReason"
              placeholder="I had issues with..."
            />
          </form>
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <Button type="button" variant="secondary"> Cancel </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="danger"
            form="downgrade-form"
            :disabled="isPending"
          >
            Downgrade
          </Button>
        </DialogFooter>
      </template>
      <template v-if="step === 'step3'">
        <div class="px-6 py-6">
          <h2>Plan successfully canceled</h2>
          <p>
            Your previous plan will remain active until
            {{ nextInvoice.date }}
            and then be converted to the Free Plan. If you change your mind you
            can always resume to the previous plan.
          </p>
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <Button type="button" variant="secondary"> Close </Button>
          </DialogClose>
        </DialogFooter>
      </template>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
// TODO build our own Checkbox component
import { CheckboxIndicator, CheckboxRoot } from 'radix-vue';
import { Check } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';

import { Button } from '@ui/components/button';
import { Textarea } from '@ui/components/textarea';
import Spinner from '@ui/components/Spinner.vue';

import requests from '@vuex/requests.js';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';

const queryClient = useQueryClient();

const step = ref('step1');
const checkedOptions = ref([]);
const options = [
  'Too expensive',
  'Found something else',
  'Not happy with product quality',
  'Technical problems',
  'Poor customer support',
  'Not interested anymore',
  'Other',
];
const store = useStore();

const { isLoading, data: nextInvoices } = useQuery({
  queryKey: ['workspaceNextInvoice'],
  queryFn: () =>
    requests.workspaces.subscription.nextInvoices(
      store.getters['workspace/getId']
    ),
});

const nextInvoice = computed(() => {
  if (nextInvoices?.value?.yearly?.date) {
    return nextInvoices.value.yearly;
  }
  if (nextInvoices?.value?.monthly?.date) {
    return nextInvoices.value.monthly;
  }
  return null;
});

// FORM
const validationSchema = toTypedSchema(
  z.object({
    reasons: z.array(z.string()).nonempty(),
  })
);

const { handleSubmit, defineField, setValues } = useForm({
  validationSchema,
  initialValues: {
    reasons: [],
  },
});
const [otherReason, otherReasonAttrs] = defineField('otherReason');

const handleChange = (event, option) => {
  if (checkedOptions.value.includes(option)) {
    checkedOptions.value = checkedOptions.value.filter(opt => opt !== option);
  } else {
    checkedOptions.value = [...checkedOptions.value, option];
  }
  setValues({ reasons: checkedOptions.value });
};

const { isPending, mutate: cancelMutation } = useMutation({
  mutationFn: async payload => {
    const res = await requests.workspaces.subscription.cancel(
      store.getters['workspace/getId'],
      { ...payload }
    );
    return res;
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.current.queryKey,
    });
    toast.success('Your subscription has been successfully canceled');
    step.value = 'step3';
  },
  onError: async () => {
    toast.error('Something went wrong, please try again later');
  },
});

const onSubmit = handleSubmit(async values => {
  const reasonsList = values.reasons;
  if (values.reasons.includes('Other')) {
    reasonsList.push(otherReason.value);
  }

  await cancelMutation({ reasons: reasonsList });
});
</script>
